import React, { createContext, useContext, useState } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IBackendCartEntries, ICartEntry } from '@rbi-ctg/menu';
import RealMealModal from 'components/real-meals/real-meal-modal';
import {
  GetUserFavoritesRefsDocument,
  ICartEntryInput,
  IGetUserFavoriteQuery,
  useCreateFavoriteMutation,
} from 'generated/rbi-graphql';
import { useLocationContext } from 'state/location';
import { useMParticleContext } from 'state/mParticle';
import { CustomEventNames, EventTypes } from 'state/mParticle/constants';
import { useOrderContext } from 'state/order';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { remappedCartForBackEnd } from 'utils/cart';
import { getCartEntryRef } from 'utils/menu/get-cart-entry-ref';
import { mapBackendToFrontend } from 'utils/reorder';
import { editFavorite, routes } from 'utils/routing';

interface ISelectFavorite {
  cartEntries: ICartEntry[];
  isRealMealFavorite: boolean;
}

export interface IFavoritesContext {
  selectFavorite: (options: ISelectFavorite) => void;
  currentCartEntry: ICartEntry | null;
  isPendingSave: boolean;
  cancelEditing: () => void;
  editName: (entry: any) => void;
  editEntry: (entry: any) => void;
  upsertEntry: (newFavorite: any) => void;
  removeEntry: (lineId: string) => void;
  setCurrentEditingFavorite: (newFavorite?: IGetUserFavoriteQuery['userFavorite'] | null) => void;
  tempFavorite: IGetUserFavoriteQuery['userFavorite'] | null;
}

// @TODO: This does not work as intended. We should infer the cartEntry type from the generated favorite fragment
// export type FavoriteEntries = NonNullable<IGetUserFavoriteQuery['userFavorite']['entries']>
// export type FavoriteEntry = FavoriteEntries extends (infer U)[] ? U : never;

export const FavoritesContext = createContext({} as IFavoritesContext);
export const useFavoritesContext = () => useContext(FavoritesContext);

export const FavoritesProvider: React.FC<IBaseProps> = ({ children }) => {
  const { addToCart } = useOrderContext();
  const [createFavorite] = useCreateFavoriteMutation();
  const [isRealMeal, setIsRealMeal] = useState<boolean>(false);
  const [tempFavorite, setTempFavorite] = useState<IFavoritesContext['tempFavorite']>(null);
  const [isPendingSave, setIsPendingSave] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentCartEntry, setCurrentCartEntry] = useState<ICartEntry | null>(null);
  const { logEvent } = useMParticleContext();
  const mapToFrontendEntry = useMapToFrontendCartEntry();

  const { navigate } = useLocationContext();

  const selectFavorite = (options: ISelectFavorite) => {
    const { cartEntries, isRealMealFavorite } = options;
    const backendEntries = remappedCartForBackEnd(cartEntries);
    backendEntries.forEach(upsertEntry);
    return isRealMealFavorite
      ? setIsRealMeal(true)
      : editFavorite({ navigate, favoriteId: tempFavorite?.favoriteId });
  };

  /**
   * Check if the favoriteId is the same or not.
   *
   * If the favoriteId is different :
   *  -(call `cancelEditing`):
   *    - reset isPendingSave to false
   *    - change tempFavorite to null since we are starting over
   *  - update the activeEditingFavoriteId
   */
  const setCurrentEditingFavorite = (
    newFavorite?: IGetUserFavoriteQuery['userFavorite'] | null
  ) => {
    if (
      !newFavorite ||
      (tempFavorite !== null && tempFavorite.favoriteId === newFavorite.favoriteId)
    ) {
      return;
    }
    cancelEditing();
    return setTempFavorite(newFavorite);
  };

  const editName = (newName: string) => {
    setTempFavorite(
      currentTempFavorite =>
        ({
          ...currentTempFavorite,
          name: newName,
        } as IFavoritesContext['tempFavorite'])
    );
    setIsPendingSave(true);
  };

  const editEntry = (entry: any) => {
    setCurrentCartEntry({
      ...mapBackendToFrontend(entry),
      // Make sure to maintain the cartId when converting
      cartId: entry.lineId,
    });
    navigate(
      `${routes.menu}/${entry.type.toLowerCase()}-${entry.sanityId}?isEdit=true&isFavorite=true`
    );
  };

  // @TODO: Figure out the type for the entries
  const upsertEntry = (newEntry: any) => {
    // if exists already remove the old one and
    // append the entry to the tempFavorite.entries
    setTempFavorite(currentTempFavorite => {
      const existingEntry = (currentTempFavorite?.entries ?? []).find(
        currentEntry => currentEntry?.lineId === newEntry?.lineId
      );

      const filteredEntries = existingEntry
        ? (currentTempFavorite?.entries ?? []).filter(
            currentEntry => currentEntry?.lineId !== newEntry?.lineId
          )
        : currentTempFavorite?.entries ?? [];

      // @ts-ignore
      const entries = [...filteredEntries, newEntry];

      return {
        ...currentTempFavorite,
        ref: getFavoriteRef({
          entries,
        }),
        entries,
      } as IFavoritesContext['tempFavorite'];
    });

    setIsPendingSave(true);
  };

  const removeEntry = (lineId: string) => {
    setTempFavorite(currentTempFavorite => {
      const entries = currentTempFavorite?.entries?.filter(
        currentEntry => currentEntry?.lineId !== lineId
      );
      return {
        ...currentTempFavorite,
        ref: getFavoriteRef({
          entries,
        }),
        entries,
      } as IFavoritesContext['tempFavorite'];
    });

    setIsPendingSave(true);
  };

  const cancelEditing = () => {
    setIsPendingSave(false);
    setIsLoading(false);
    setTempFavorite(null);
  };

  const getFavoriteRef = (favorite?: Partial<IGetUserFavoriteQuery['userFavorite']> | null) => {
    let ref = null;
    if (favorite?.entries?.length === 1) {
      ref = getCartEntryRef((favorite?.entries[0] as unknown) as ICartEntry);
    }
    return ref;
  };

  const onSaveRealMeal = async (name: string) => {
    // Save real meal favorite
    createFavorite({
      variables: {
        input: {
          name,
          ref: tempFavorite?.ref,
          entries: (tempFavorite?.entries as unknown) as ICartEntryInput[],
        },
      },
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });

    setIsLoading(true);

    // Add entries to cart
    for (const entry of tempFavorite?.entries || []) {
      // Convert favorite entry to frontend entry
      const { cartEntry } = await mapToFrontendEntry((entry as unknown) as IBackendCartEntries);
      addToCart(cartEntry);
    }
    // Clean real meals and favorites
    onDismissRealMeal();

    // Log event into mParticle
    logEvent(CustomEventNames.BUTTON_CLICK_SAVE_REAL_MEAL, EventTypes.Other);

    // Redirect to the menu
    navigate(routes.menu);
  };

  const onDismissRealMeal = () => {
    setIsRealMeal(false);
    cancelEditing();
  };

  return (
    <FavoritesContext.Provider
      value={{
        selectFavorite,
        currentCartEntry,
        cancelEditing,
        editName,
        editEntry,
        isPendingSave,
        removeEntry,
        upsertEntry,
        setCurrentEditingFavorite,
        tempFavorite,
      }}
    >
      {children}
      {isRealMeal && (
        <RealMealModal
          isLoading={isLoading}
          onSave={name => onSaveRealMeal(name)}
          onDismiss={() => onDismissRealMeal()}
        />
      )}
    </FavoritesContext.Provider>
  );
};
